import { AlertTriangle, CheckCircle2, CircleDot, Info, Loader2, PauseCircle, XCircle } from 'lucide-react';
import type { StreamState, StreamStatus } from '@shared/protocol';
import type { Level } from '../lib/meterMath';

const levelIcon = { pass: CheckCircle2, warn: AlertTriangle, fail: XCircle, info: Info } as const;

/** Assessment badge: colour is always paired with an icon and text. */
export function LevelBadge({ level, children }: { level: Level; children: React.ReactNode }) {
  const Icon = levelIcon[level];
  return (
    <span className={`badge badge-${level}`}>
      <Icon size={14} aria-hidden />
      {children}
    </span>
  );
}

const statusInfo: Record<StreamStatus, { label: string; cls: string; Icon: typeof Info }> = {
  idle: { label: 'Not monitoring', cls: 'badge-neutral', Icon: PauseCircle },
  connecting: { label: 'Connecting', cls: 'badge-info', Icon: Loader2 },
  live: { label: 'Live', cls: 'badge-pass', Icon: CircleDot },
  stalled: { label: 'Stalled', cls: 'badge-warn', Icon: AlertTriangle },
  ended: { label: 'Ended', cls: 'badge-warn', Icon: PauseCircle },
  error: { label: 'Error', cls: 'badge-fail', Icon: XCircle },
};

export function StreamStatusBadge({ state }: { state: StreamState | null | undefined }) {
  const s = statusInfo[state?.status ?? 'idle'];
  return (
    <span className={`badge ${s.cls}`} title={state?.message ?? undefined}>
      <s.Icon size={14} aria-hidden className={state?.status === 'connecting' ? 'spin' : undefined} />
      {s.label}
    </span>
  );
}

export function Notice({ level, children }: { level: Level; children: React.ReactNode }) {
  const Icon = levelIcon[level];
  return (
    <div className={`notice notice-${level}`} role={level === 'fail' ? 'alert' : 'status'}>
      <Icon size={16} aria-hidden />
      <div>{children}</div>
    </div>
  );
}
